import { Anchor, Card, Code, Group, SegmentedControl, SimpleGrid, Stack, Text, Title } from "@mantine/core";
import { useQuery } from "@tanstack/react-query";
import { useState } from "react";
import { Link, useParams } from "react-router";
import { ErrorBox, notifyError } from "../components/Errors";
import { CancelButton, JobProgress, JobStatusBadge, JobsTable, useKindLabel } from "../components/Jobs";
import { PageTitle } from "../components/Layout";
import { FitPlots, fitSummary, ReadoutPlots } from "../components/DevicePlots";
import { Plot } from "../components/Plot";
import { fitFigure } from "../components/SkfitterPlots";
import { TransportPlots } from "../components/TransportPlots";
import type { FitResult } from "../lib/skfitter";
import type { PropResult } from "../lib/skfitter";
import { ResultTabs } from "./skfitter/PropertiesStep";
import { fitMetrics } from "./skfitter/ResultStep";
import { download, formatDate, formatDuration, get, type Job } from "../lib/api";
import { useLiveJob } from "../lib/hooks";

export function JobsPage() {
  const [filter, setFilter] = useState("all");
  const q = useQuery({
    queryKey: ["jobs", filter],
    queryFn: () => get<Job[]>(`/jobs?limit=100${filter === "active" ? "&active=true" : ""}`),
    refetchInterval: (s) => (s.state.data?.some((j) => j.status === "queued" || j.status === "running") ? 3000 : false),
  });
  return (
    <>
      <PageTitle title="Calculations">
        <SegmentedControl value={filter} onChange={setFilter} data={[{ value: "all", label: "All" }, { value: "active", label: "Running" }]} />
      </PageTitle>
      <Card withBorder>
        <ErrorBox error={q.error} />
        <JobsTable jobs={q.data ?? []} />
      </Card>
    </>
  );
}

function Fact({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <Stack gap={0}>
      <Text size="xs" c="dimmed">{label}</Text>
      <Text size="sm">{children}</Text>
    </Stack>
  );
}

export function JobDetail() {
  const { id } = useParams();
  const { job, error } = useLiveJob(id);
  const label = useKindLabel();
  if (error) return <ErrorBox error={error} />;
  if (!job) return null;
  const result = job.result;
  return (
    <Stack>
      <PageTitle title={label(job.kind)}>
        <JobStatusBadge status={job.status} />
        <CancelButton job={job} />
      </PageTitle>
      <Card withBorder>
        <SimpleGrid cols={{ base: 2, sm: 3, md: 6 }}>
          <Fact label="Submitted">{formatDate(job.created_at, true)}</Fact>
          <Fact label="Finished">{formatDate(job.finished_at, true)}</Fact>
          <Fact label="CPU time">{job.finished_at ? formatDuration(job.cpu_seconds) : "-"}</Fact>
          <Fact label="Time limit">{formatDuration(job.time_limit_s)}</Fact>
          <Fact label="Queue">{job.queue}</Fact>
          <Fact label="Project">{job.project_id ? <Anchor component={Link} to={`/projects/${job.project_id}`}>open</Anchor> : "-"}</Fact>
        </SimpleGrid>
        <JobProgress job={job} />
        {job.error && <ErrorBox error={new Error(job.error)} />}
      </Card>
      {result && job.kind === "transport.compute" && (
        <Card withBorder>
          <Group justify="space-between" mb="xs">
            <Title order={4}>Results</Title>
            <Group gap="md">
              {result.export_file && (
                <Anchor component="button" size="sm"
                  onClick={() => download(`/files/${result.export_file}/download`, `transport_${result.tab ?? result.material}_${result.mode}_export.zip`).catch(notifyError)}>
                  Data & figures (.zip)
                </Anchor>
              )}
              {result.arrays_file && (
                <Anchor component="button" size="sm"
                  onClick={() => download(`/files/${result.arrays_file}/download`, `transport_${result.tab ?? result.material}_${result.mode}.npz`).catch(notifyError)}>
                  Arrays (.npz)
                </Anchor>
              )}
            </Group>
          </Group>
          <TransportPlots result={result} />
        </Card>
      )}
      {result && (job.kind === "transport.characterize" || job.kind === "transport.t1sim") && (
        <Card withBorder>
          <Group justify="space-between" mb="xs">
            <Title order={4}>Results</Title>
            {result.export_file && (
              <Anchor component="button" size="sm"
                onClick={() => download(`/files/${result.export_file}/download`,
                  job.kind === "transport.t1sim" ? `t1sim_${result.tab}_${result.protocol}_export.zip` : `characterize_${result.tab}_export.zip`).catch(notifyError)}>
                Data & figures (.zip)
              </Anchor>
            )}
          </Group>
          {job.kind === "transport.characterize" ? (
            <Stack gap="xs">
              <Text size="sm">{fitSummary(result as any)}</Text>
              <FitPlots result={result as any} />
            </Stack>
          ) : <ReadoutPlots result={result as any} />}
        </Card>
      )}
      {result && (job.kind === "skfitter.fit" || job.kind === "skfitter.load") && result.rows && (
        <Card withBorder>
          <Group justify="space-between" mb="xs">
            <Title order={4}>Results</Title>
            {result.files?.zip && (
              <Anchor component="button" size="sm"
                onClick={() => download(`/files/${result.files.zip}/download`, `${result.seed}_fit_results.zip`).catch(notifyError)}>
                Fit files (.zip)
              </Anchor>
            )}
          </Group>
          <SkFit result={result as FitResult} />
        </Card>
      )}
      {result && job.kind === "skfitter.properties" && (result as PropResult).items?.length > 0 && (
        <Card withBorder>
          <Title order={4} mb="xs">Results</Title>
          {result.stopped && <Text size="sm" c="orange">{result.stopped}</Text>}
          <ResultTabs items={(result as PropResult).items} />
        </Card>
      )}
      {job.params && (
        <Card withBorder>
          <Text fw={600} mb="xs">Input</Text>
          <Code block>{JSON.stringify(job.params, null, 2)}</Code>
        </Card>
      )}
      {job.log && (
        <Card withBorder>
          <Text fw={600} mb="xs">Log</Text>
          <Code block style={{ maxHeight: 300, overflow: "auto" }}>{job.log}</Code>
        </Card>
      )}
    </Stack>
  );
}

function SkFit({ result }: { result: FitResult }) {
  const fig = fitFigure(result);
  return (
    <Stack gap="xs">
      <Plot data={fig.data} layout={fig.layout} height={440} />
      <Text size="sm" c="dimmed">{fitMetrics(result)}</Text>
    </Stack>
  );
}
